import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';
import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {PublishersService} from '../../services/publishers.service';
import {Domain, DomainListRequest} from '../../domain/domain.model';
import {SortUtils} from '../../utils/sort-utils';
import {DomainsService} from '../../services/domains.service';

@Component({
  selector: 'ctv-domain-autocomplete',
  template: `
    <mat-form-field [floatLabel]="floatLabel || 'always'" class="w-100" [ngClass]="formFieldClass" [appearance]="formFieldAppearance ? formFieldAppearance : undefined">
      <mat-label *ngIf="floatLabel !== 'never'">{{label ? label : 'Select domain'}}</mat-label>
      <input matInput
             #url
             aria-label="Domain"
             (focus)="onFocusEvent()"
             [matAutocomplete]="auto"
             [formControl]="filterCtrl"
             placeholder="{{floatLabel !== 'never' ? 'Search' : 'Select domain'}}">
      <i class="fas fa-spinner fa-spin" matSuffix [hidden]="!serverSearching"></i>
      <mat-autocomplete #auto="matAutocomplete"
                        (optionSelected)="optionSelected($event)"
                        [displayWith]="display">
        <mat-option *ngFor="let item of filteredItems | async" [value]="item">
          {{item.url}}
        </mat-option>
      </mat-autocomplete>
      <mat-hint *ngIf="allDomains?.length === 0 && loading === false" class="text-warning">Publisher doesn't have any domains</mat-hint>
    </mat-form-field>`,
})
export class DomainAutocompleteComponent implements OnInit {
  @ViewChild('url', {static: true}) urlFilter: ElementRef;

  @Input()
  public placeholder: string;

  @Input()
  public url: string;

  @Input()
  public floatLabel: string;

  @Input()
  public label: string;

  @Input()
  public formFieldClass: string;

  @Input()
  public formFieldAppearance: string;

  @Input()
  public publisherIds: string[];

  @Input()
  public items: Domain[];

  @Input()
  public pub: boolean;

  @Output()
  domainSelected = new EventEmitter<Domain>();

  serverSearching = false;

  filteredItems: Subject<Domain[]>;
  filterCtrl = new UntypedFormControl();
  selectedDomain;
  allDomains: Domain[] = [];
  filteredDomainIds: string[] = [];
  loading = false;


  constructor(private domainService: DomainsService) {
    this.loading = true;
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges.pipe(
      debounceTime(800),
      distinctUntilChanged())
      .subscribe(next => {
        this.filteredItems.next([]);
        if (!next && this.selectedDomain) {
          this.selectedDomain = undefined;
          this.emit();
        }

        if (next && typeof next !== 'object') {
          this.serverSearching = true;
          this.filterCtrl.disable();
          this.allDomains.length === 0 ? this.getAllDomains() : this.searchDomain();
        }
      });
  }

  resetInput() {
    this.selectedDomain = null;
    this.filterCtrl.setValue(null);
  }

  ngOnInit(): void {
    this.getAllDomains();
    if (this.url) {
      const dom = Domain.of(this.url);
      this.selectedDomain = dom;
      this.filterCtrl.setValue(dom);
      this.emit();
    }
  }

  private httpCall(filter: any): any{
    if (this.pub) {
      return this.getPubDomainsList(filter);
    }
    else {
      return this.getDomainsList(filter);
    }
  }

  private getDomainsList(filter: any): any {
    return this.domainService.getAllDomains(filter);
  }

  private getPubDomainsList(filter: any): any {
    return this.domainService.getAllPubDomains(filter);
  }

  display(p?: Domain) {
    return p ? p.url : undefined;
  }

  optionSelected($event) {
    this.selectedDomain = $event.option.value;
    this.filteredItems.next([]);
    this.urlFilter.nativeElement.blur();
    this.emit();
  }

  private emit() {
    this.domainSelected.emit(this.selectedDomain);
  }

  getAllDomains() {
    this.httpCall({publisherIds: this.publisherIds}).subscribe(
      response => {
        this.allDomains = response.content;
      },
      error => {
        console.log(error);
      },
    () => {
      this.serverSearching = false;
      this.filterCtrl.enable();
      this.loading = false;
    }
    );
  }

  onFocusEvent() {
    if (!this.filterCtrl.value?.length) {
      this.filteredItems.next(this.allDomains.filter(item => !this.filteredDomainIds.includes(item.url)));
    }
  }

  searchDomain() {
    if (this.allDomains.length > 0) {
      const search = this.allDomains.filter(domain => domain.url.toLowerCase().includes(this.filterCtrl.value.toLowerCase()));
      this.filteredItems.next(
        search.filter(
          (item) => !this.filteredDomainIds.includes(item.url)
        )
      );
    }

    this.filterCtrl.enable();
    this.urlFilter.nativeElement.focus();
    this.serverSearching = false;
    this.loading = true;
  }

  //Fill the list with choosen domains so we dont show them again on dropdown list
  updateFilteredDomains(items) {
    this.filteredDomainIds = items.map(item => item.url);
  }

  disableSearch(disable: true) {
    disable ? this.filterCtrl.disable() : this.filterCtrl.enable();
  }
}
